import Controller, { inject as controller } from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import type { AsyncBelongsTo } from '@ember-data/model';
import type StoreService from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import { task, dropTask } from 'ember-concurrency';
import type Entry from 'garaje/models/entry';
import type LocationsCapacity from 'garaje/models/locations-capacity';
import type SignInFieldPage from 'garaje/models/sign-in-field-page';
import type CurrentAdminService from 'garaje/services/current-admin';
import type CurrentLocationService from 'garaje/services/current-location';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type MetricsService from 'garaje/services/metrics';
import type WorkplaceMetricsService from 'garaje/services/workplace-metrics';
import { entryApprovalMessage } from 'garaje/utils/entry-approval-message';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import { fetchCapacity } from 'garaje/utils/locations-capacity';
import _intersection from 'lodash/intersection';
import { reads } from 'macro-decorators';

import type WorkplaceEntriesController from '../controller';

export default class WorkplaceEntriesIndexController extends Controller {
  @service declare currentLocation: CurrentLocationService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare flashMessages: FlashMessagesService;
  @service declare metrics: MetricsService;
  @service declare store: StoreService;
  @service declare workplaceMetrics: WorkplaceMetricsService;

  @controller('workplace.entries') declare entries: WorkplaceEntriesController;

  @reads('entries.eventReports') eventReports!: WorkplaceEntriesController['eventReports'];
  @reads('entries.isToday') isToday!: WorkplaceEntriesController['isToday'];
  @reads('entries.isAfterToday') isAfterToday!: WorkplaceEntriesController['isAfterToday'];

  @tracked hoveringTable = false;

  get signInFieldPages(): AsyncBelongsTo<SignInFieldPage>[] | undefined {
    return this.currentLocation.location.flows.content?.map((flow) => flow.signInFieldPage);
  }

  get showVideo(): boolean {
    const canSeeVideo = isPresent(_intersection(['Global Admin', 'Location Admin'], this.currentAdmin.roleNames));
    return this.entries.noHistoricVisitors && canSeeVideo;
  }

  get sortField(): string {
    return this.entries.sort.replace(/^[-|+]/g, '');
  }

  get sortDirection(): 'asc' | 'desc' {
    return this.entries.sort.startsWith('-') ? 'desc' : 'asc';
  }

  get menuHeight(): string {
    const header = document.querySelector('.employeesTable-header');
    if (!header) return '0px';

    // Keep a minimum of 13px space from the table
    return `${header.clientHeight + 13}px`;
  }

  get isSelectAllIndeterminate(): boolean {
    const selectedCount = this.entries.selectedEntries.length;
    const totalEntries = this.entries.relevantEntries.length;
    return selectedCount > 0 && selectedCount !== totalEntries;
  }

  get allEntriesSelected(): boolean {
    const selectedCount = this.entries.selectedEntries.length;
    const totalEntries = this.entries.relevantEntries.length;
    return selectedCount === totalEntries;
  }

  @action
  clearAll(): void {
    return this.entries.clearAll();
  }

  @action
  selectedDidChange(selected: Entry[]): void {
    return this.entries.selectedDidChange(selected);
  }

  @action
  deleteEntry(entry: Entry): void {
    return this.entries.deleteEntry(entry);
  }

  @action
  deleteEntries(entries: Entry[]): void {
    return this.entries.deleteEntries(entries);
  }

  @action
  loadMore(): Promise<void> {
    return this.entries.loadMore();
  }

  @action
  sortEntries(field: string, direction: string): void {
    const entriesController = this.entries;
    return entriesController.sortEntries(field, direction);
  }

  @action
  selectAllEntries(): void {
    return this.entries.selectAllEntries();
  }

  async refreshLocationsCapacity(date: Date): Promise<LocationsCapacity | undefined> {
    const currentLocation = this.currentLocation.location;
    if (currentLocation.capacityLimitEnabled) {
      return fetchCapacity(this.store, currentLocation, date);
    }

    return;
  }

  linkToRoute(entry: Entry): string {
    if (entry.signedInAt == null) {
      return 'workplace.scheduled';
    }
    return 'workplace.entry';
  }

  signOutTask = dropTask(async (entry: Entry, actionOrigin: string) => {
    this.metrics.trackEvent('Dashboard Entry - Signed Out', { action_origin: actionOrigin });
    this.workplaceMetrics.trackEvent('EMPLOYEE_LOG_ENTRY_SIGN_OUT_BUTTON_CLICKED', {
      entry: entry.id,
      action_origin: actionOrigin,
    });
    await this.entries.signOutEntry.perform(entry);
    await this.refreshLocationsCapacity(entry.signedInAt);
  });

  approveEntryTask = task(async (entry: Entry) => {
    try {
      await entry.approve();
      const flow = await entry.flow;
      await flow.badge; // load async relationship for entryApprovalMessage helper dependency
      this.flashMessages.showAndHideFlash('success', 'Access approved', entryApprovalMessage(entry));

      this.metrics.trackEvent('Dashboard Entry - Reviewed', {
        action: 'approve',
        entry_id: entry.id,
        source: 'Visitor Log',
        ...this.#sourcesForReviewMetrics(entry),
      });
      await entry.reload();
    } catch (error) {
      this.flashMessages.showAndHideFlash('error', 'Error approving entry', parseErrorForDisplay(error));
    } finally {
      const currentLocation = this.currentLocation.location;
      if (currentLocation?.capacityLimitEnabled) {
        await fetchCapacity(this.store, currentLocation, entry.signedInAt);
      }
    }
  });

  denyEntryTask = task(async (entry: Entry) => {
    try {
      await entry.deny();
      this.flashMessages.showAndHideFlash('warning', 'Entry denied');
      await entry.reload();

      this.metrics.trackEvent('Dashboard Entry - Reviewed', {
        action: 'deny',
        entry_id: entry.id,
        source: 'Visitor Log',
        ...this.#sourcesForReviewMetrics(entry),
      });
    } catch (_e) {
      this.flashMessages.showAndHideFlash('error', 'Error denying entry');
    }
  });

  #sourcesForReviewMetrics(entry: Entry): Record<string, boolean> {
    return (entry.visitApproval?.failedResults || []).reduce(
      (sources, report) => ({ ...sources, [report.screenerSource]: true }),
      {},
    );
  }
}
